import { Plus, Receipt, Search, Settings, Wallet } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/common/Button';
import { FormField } from '../../components/common/FormField';
import { LoadingState } from '../../components/common/LoadingState';
import { Modal } from '../../components/common/Modal';
import { createTransaction, getCourseSignups, getCourses, getStudents, getTransactions, linkTransactionToStudent } from '../../firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { useTenant } from '../../hooks/useTenant';
import { useToast } from '../../contexts/ToastContext';
import { formatCurrency } from '../../utils/currency';
import { formatDate, formatDateTime } from '../../utils/date';
import { sendFeePaymentCommunications } from '../../services/communicationService';

function toDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function toInputDateValue(value = new Date()) {
  const date = value?.toDate ? value.toDate() : value;
  const normalized = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(normalized.getTime())) return '';
  const offset = normalized.getTimezoneOffset() * 60000;
  return new Date(normalized.getTime() - offset).toISOString().slice(0, 10);
}

function normalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

function formatName(record) {
  return record?.fullName
    || `${record?.firstName || ''} ${record?.lastName || ''}`.trim()
    || record?.bio
    || 'Unnamed student';
}

function getInitials(name = '') {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
}

function formatMethod(value) {
  const text = String(value || 'cash').replace(/[_-]+/g, ' ').trim();
  return text ? `${text[0].toUpperCase()}${text.slice(1)}` : 'Cash';
}

function formatPaymentDate(value) {
  const date = toDate(value);
  return date ? formatDate(date) : '-';
}

function buildStudentKey(record) {
  return `${record.source}:${record.id}`;
}

function generateReceiptNumber() {
  const now = new Date();
  const yearPart = now.getFullYear().toString().slice(-2);
  const monthPart = String(now.getMonth() + 1).padStart(2, '0');
  const randomPart = String(Math.floor(1000 + Math.random() * 9000));
  return `SF${yearPart}${monthPart}${randomPart}`;
}

export function SchoolFeeIncomePage() {
  const { activeTenantId, activeTenant } = useTenant();
  const { user } = useAuth();
  const { notify } = useToast();
  const navigate = useNavigate();
  const currency = activeTenant?.settings?.currency || 'KES';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [students, setStudents] = useState([]);
  const [signups, setSignups] = useState([]);
  const [courses, setCourses] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [methodFilter, setMethodFilter] = useState('All');
  const [values, setValues] = useState({
    studentKey: '',
    courseId: '',
    amount: '',
    paymentDate: toInputDateValue(),
    paymentMethod: 'cash',
    reference: '',
    note: '',
  });

  const load = useCallback(async () => {
    if (!activeTenantId) {
      setStudents([]);
      setSignups([]);
      setCourses([]);
      setTransactions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [nextStudents, nextSignups, nextCourses, loadedTransactions] = await Promise.all([
        getStudents(activeTenantId),
        getCourseSignups(activeTenantId),
        getCourses(activeTenantId),
        getTransactions(activeTenantId),
      ]);
      const signupByEmail = new Map(nextSignups
        .filter((signup) => signup.authUid && (signup.email || signup.authEmail))
        .map((signup) => [normalizeText(signup.email || signup.authEmail), signup.authUid]));
      const legacyPayments = loadedTransactions.filter((transaction) => {
        const email = normalizeText(transaction.studentEmail);
        return transaction.category === 'school_fee' && !transaction.authUid && signupByEmail.has(email);
      });
      if (legacyPayments.length) {
        await Promise.allSettled(legacyPayments.map((transaction) => linkTransactionToStudent(
          activeTenantId,
          transaction.id,
          signupByEmail.get(normalizeText(transaction.studentEmail)),
        )));
      }
      setStudents(nextStudents);
      setSignups(nextSignups);
      setCourses(nextCourses.filter((course) => course.status !== 'deleted'));
      setTransactions(loadedTransactions.map((transaction) => {
        const authUid = signupByEmail.get(normalizeText(transaction.studentEmail));
        return authUid && !transaction.authUid ? { ...transaction, authUid } : transaction;
      }));
    } catch (error) {
      console.error('Unable to load school fee income', error);
      setStudents([]);
      setSignups([]);
      setCourses([]);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [activeTenantId]);

  useEffect(() => {
    load().catch((error) => {
      console.error(error);
      setLoading(false);
    });
  }, [load]);

  const studentOptions = useMemo(() => {
    const nextOptions = [];
    const seen = new Set();

    const pushRecord = (record) => {
      const name = formatName(record);
      const key = buildStudentKey(record);
      const dedupeKey = record.email
        ? `email:${normalizeText(record.email)}`
        : record.authUid
          ? `auth:${record.authUid}`
          : key;

      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);

      nextOptions.push({
        key,
        source: record.source,
        id: record.id,
        label: name,
        courseId: record.courseId || '',
        courseName: record.courseName || '',
        email: record.email || '',
        phone: record.phone || '',
        authUid: record.authUid || '',
        helper: record.intakeLabel || record.courseName || record.course || '',
      });
    };

    signups.forEach((item) => pushRecord({ ...item, source: 'application' }));
    students.forEach((item) => pushRecord({ ...item, source: 'student' }));

    return nextOptions.sort((a, b) => a.label.localeCompare(b.label));
  }, [signups, students]);

  const transactionRows = useMemo(() => {
    const search = normalizeText(searchTerm);
    const people = [...students, ...signups];
    const resolveStudent = (transaction) => people.find((person) => [person.id, person.authUid, person.email, person.authEmail]
      .filter(Boolean)
      .some((value) => normalizeText(value) === normalizeText(transaction.studentId || transaction.authUid || transaction.studentEmail)));

    return transactions
      .map((item) => {
        const linkedStudent = resolveStudent(item);
        const linkedSignup = signups.find((signup) => [signup.id, signup.authUid, signup.email, signup.authEmail]
          .filter(Boolean)
          .some((value) => normalizeText(value) === normalizeText(item.studentId || item.authUid || item.studentEmail)));
        const linkedCourse = courses.find((course) => course.id === item.courseId);
        return {
          ...item,
          studentName: item.studentName || formatName(linkedStudent),
          studentEmail: item.studentEmail || linkedStudent?.email || linkedStudent?.authEmail || '',
          studentPhone: item.studentPhone || linkedStudent?.phone || linkedStudent?.phoneNumber || '',
          courseName: item.courseName || linkedCourse?.name || linkedStudent?.courseName || '',
          studentRecordId: linkedSignup?.id || linkedStudent?.id || item.studentId || '',
        };
      })
      .filter((item) => String(item.category || 'school_fee') === 'school_fee')
      .filter((item) => {
        const matchesSearch = !search || [
          item.studentName,
          item.courseName,
          item.reference,
          item.paymentMethod,
          item.note,
          item.studentEmail,
        ].some((value) => normalizeText(value).includes(search));

        const matchesMethod = methodFilter === 'All' || normalizeText(item.paymentMethod) === normalizeText(methodFilter);
        return matchesSearch && matchesMethod;
      });
  }, [courses, methodFilter, searchTerm, signups, students, transactions]);

  const selectedStudent = useMemo(
    () => studentOptions.find((item) => item.key === values.studentKey) || null,
    [studentOptions, values.studentKey],
  );

  const selectedCourse = useMemo(
    () => courses.find((course) => course.id === values.courseId) || null,
    [courses, values.courseId],
  );

  const totalIncome = useMemo(
    () => transactionRows.reduce((sum, item) => sum + Number(item.amount || 0), 0),
    [transactionRows],
  );

  const monthIncome = useMemo(() => {
    const now = new Date();
    return transactionRows.reduce((sum, item) => {
      const paidDate = toDate(item.paymentDate) || toDate(item.createdAt);
      if (!paidDate) return sum;
      return paidDate.getMonth() === now.getMonth() && paidDate.getFullYear() === now.getFullYear()
        ? sum + Number(item.amount || 0)
        : sum;
    }, 0);
  }, [transactionRows]);

  const recordCount = transactionRows.length;
  const studentCount = useMemo(
    () => new Set(transactionRows.map((item) => item.studentId).filter(Boolean)).size,
    [transactionRows],
  );

  const paymentDateLabel = useMemo(() => {
    const now = new Date();
    return now.toLocaleString('en-KE', { month: 'long', year: 'numeric' });
  }, []);

  const heroSubtitle = `Record collected school fees, keep the payment trail tidy, and review income for ${activeTenant?.name || 'your academy'}.`;

  function openModal() {
    setValues({
      studentKey: studentOptions[0]?.key || '',
      courseId: studentOptions[0]?.courseId || '',
      amount: '',
      paymentDate: toInputDateValue(),
      paymentMethod: 'cash',
      reference: '',
      note: '',
    });
    setOpen(true);
  }

  function handleStudentChange(nextKey) {
    const nextStudent = studentOptions.find((item) => item.key === nextKey) || null;
    setValues((current) => ({
      ...current,
      studentKey: nextKey,
      courseId: nextStudent?.courseId || current.courseId,
    }));
  }

  function handleCourseChange(courseId) {
    setValues((current) => ({ ...current, courseId }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!selectedStudent || !values.courseId || !values.amount || !values.paymentDate) return;

    setSaving(true);
    try {
      const transactionCourse = selectedCourse || courses.find((course) => course.id === values.courseId) || null;
      const receiptNumber = generateReceiptNumber();
      const payment = {
        receiptNumber,
        studentName: selectedStudent.label,
        studentEmail: selectedStudent.email,
        studentPhone: selectedStudent.phone,
        courseName: transactionCourse?.name || '',
        amount: values.amount,
        paymentDate: values.paymentDate,
        paymentMethod: values.paymentMethod,
        reference: values.reference,
      };
      await createTransaction(activeTenantId, user?.uid, {
        ...payment,
        type: 'revenue',
        category: 'school_fee',
        source: 'manual',
        studentId: selectedStudent.id,
        authUid: selectedStudent.authUid || null,
        courseId: transactionCourse?.id || values.courseId,
        note: values.note,
        status: 'posted',
      });

      const communicationResults = await sendFeePaymentCommunications({
        user,
        payment,
        academyName: activeTenant?.name,
        currency,
      });
      communicationResults.forEach((result, index) => {
        if (result.status === 'rejected') {
          console.error(`${index === 0 ? 'Fee payment email' : 'Fee payment SMS'} could not be sent`, result.reason);
        }
      });

      notify('School fee recorded successfully.');
      setOpen(false);
      await load();
    } catch (error) {
      console.error('Unable to record school fee', error);
      notify('Unable to record school fee. Try again.', 'error');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingState label="Loading school fee income..." />;

  return (
    <div className="clientsComp incomePage">
      <div className="incomeHero">
        <div className="incomeHeroCopy">
          <div className="incomeHeroTitleRow">
            <p className="appsKicker">Finance</p>
            <h5>School Fee Income</h5>
          </div>
          <p className="incomeHeaderMeta">
            <span>{activeTenant?.name || 'Academy'}</span>
            <span className="incomeInlineDot" />
            <span>{paymentDateLabel}</span>
          </p>
          <p className="incomeHeroText">{heroSubtitle}</p>
        </div>
        <div className="incomeHeroActions">
          <Button icon={Settings} variant="secondary" onClick={() => navigate('/dashboard/settings?tab=fee-payment')}>Set up student payments</Button>
          <Button icon={Plus} onClick={openModal}>Record School Fee</Button>
        </div>
      </div>

      <div className="incomeStatsGrid">
        <article className="incomeStatCard incomeStatPrimary">
          <span className="incomeStatLabel">Total School Fee Income</span>
          <h3>{formatCurrency(totalIncome, currency)}</h3>
          <p>All posted school fee receipts in this academy.</p>
        </article>
        <article className="incomeStatCard">
          <span className="incomeStatLabel">This Month</span>
          <h3>{formatCurrency(monthIncome, currency)}</h3>
          <p>Money collected during the current calendar month.</p>
        </article>
        <article className="incomeStatCard">
          <span className="incomeStatLabel">Records</span>
          <h3>{recordCount}</h3>
          <p>Individual school fee entries logged so far.</p>
        </article>
        <article className="incomeStatCard">
          <span className="incomeStatLabel">Paying Students</span>
          <h3>{studentCount}</h3>
          <p>Unique students or applicants with fee records.</p>
        </article>
      </div>

      <div className="incomeToolbar">
        <label className="incomeSearch" htmlFor="school-fee-search">
          <span className="incomeSearchIcon" aria-hidden="true"><Search size={16} /></span>
          <input
            id="school-fee-search"
            type="text"
            placeholder="Search student, course, receipt..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </label>
        <div className="incomeToolbarMeta">
          <span className="incomeToolbarText">{recordCount} school fee record{recordCount === 1 ? '' : 's'}</span>
          <span className="incomeInlineDot" />
          <span className="incomeToolbarText">{studentOptions.length} students / applicants available</span>
        </div>
        <select className="incomeSelect" value={methodFilter} onChange={(event) => setMethodFilter(event.target.value)}>
          <option value="All">All Methods</option>
          <option value="cash">Cash</option>
          <option value="mpesa">M-Pesa</option>
          <option value="bank transfer">Bank Transfer</option>
          <option value="card">Card</option>
          <option value="cheque">Cheque</option>
          <option value="other">Other</option>
        </select>
      </div>

      {transactionRows.length ? (
        <section className="incomeTableCard">
          <div className="incomeTableHeader">
            <div>
              <h6>School Fee Records</h6>
              <p>Track the student, course, amount, method, and reference for each payment.</p>
            </div>
            <span>{transactionRows.length} visible record{transactionRows.length === 1 ? '' : 's'}</span>
          </div>
          <div className="incomeTableWrap">
            <table className="incomeRecordsTable">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Student</th>
                  <th>Course</th>
                  <th>Amount</th>
                  <th>Method</th>
                  <th>Receipt</th>
                </tr>
              </thead>
              <tbody>
                {transactionRows.map((record) => (
                  <tr key={record.id}>
                    <td>
                      <div className="incomeDateCell">
                        <strong>{formatPaymentDate(record.paymentDate || record.createdAt)}</strong>
                        <span>{formatDateTime(record.createdAt)}</span>
                      </div>
                    </td>
                    <td>
                      <div className="incomeStudentCell">
                        <div className="incomeAvatar" aria-hidden="true">{getInitials(record.studentName)}</div>
                        <div>
                          {record.studentRecordId ? <Link className="incomeStudentName" to={`/dashboard/applications/${record.studentRecordId}`}>{record.studentName || 'Unknown student'}</Link> : <p className="incomeStudentName">{record.studentName || 'Unknown student'}</p>}
                          <span className="incomeStudentMeta">{record.studentEmail || record.studentPhone || 'No contact saved'}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="incomeCoursePill">{record.courseName || 'Course not set'}</span>
                    </td>
                    <td>
                      <span className="incomeAmountText">{formatCurrency(record.amount || 0, currency)}</span>
                    </td>
                    <td>
                      <span className="incomeMethodBadge">{formatMethod(record.paymentMethod)}</span>
                    </td>
                    <td>
                      <Button
                        to={`/dashboard/finance/school-fee-income/${record.id}/receipt`}
                        variant="secondary"
                        icon={Receipt}
                        className="appsViewBtn"
                      >
                        View Receipt
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : (
        <div className="incomeStateCard incomeEmptyState">
          <Wallet size={28} />
          <h6>No school fee records yet.</h6>
          <p>Use the record button to post the first school fee payment for a student or applicant.</p>
          <Button icon={Plus} onClick={openModal}>Record School Fee</Button>
        </div>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Record School Fee"
        description="Add a school fee payment against a student or applicant."
      >
        <form className="modal-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            <FormField label="Student / Applicant" id="income-student">
              <select id="income-student" value={values.studentKey} onChange={(event) => handleStudentChange(event.target.value)} required>
                <option value="">Select a person</option>
                {studentOptions.map((student) => (
                  <option key={student.key} value={student.key}>
                    {student.label}{student.courseName ? ` • ${student.courseName}` : ''}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Course" id="income-course">
              <select id="income-course" value={values.courseId} onChange={(event) => handleCourseChange(event.target.value)} required>
                <option value="">Select a course</option>
                {courses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.name}{course.code ? ` (${course.code})` : ''}
                  </option>
                ))}
              </select>
            </FormField>
          </div>

          {selectedStudent ? (
            <div className="incomeSelectedStudent">
              <div>
                <strong>{selectedStudent.label}</strong>
                <span>{selectedStudent.email || selectedStudent.phone || 'No contact saved'}</span>
              </div>
              <div>
                <strong>{selectedStudent.helper || 'Student record'}</strong>
                <span>Selected for fee posting</span>
              </div>
            </div>
          ) : null}

          <div className="form-grid">
            <FormField label="Amount" id="income-amount">
              <input id="income-amount" type="number" min="0" step="1" required value={values.amount} onChange={(event) => setValues({ ...values, amount: event.target.value })} placeholder="0" />
            </FormField>
            <FormField label="Payment Date" id="income-date">
              <input id="income-date" type="date" required value={values.paymentDate} onChange={(event) => setValues({ ...values, paymentDate: event.target.value })} />
            </FormField>
          </div>

          <div className="form-grid">
            <FormField label="Payment Method" id="income-method">
              <select id="income-method" value={values.paymentMethod} onChange={(event) => setValues({ ...values, paymentMethod: event.target.value })}>
                <option value="cash">Cash</option>
                <option value="mpesa">M-Pesa</option>
                <option value="bank transfer">Bank Transfer</option>
                <option value="card">Card</option>
                <option value="cheque">Cheque</option>
                <option value="other">Other</option>
              </select>
            </FormField>
            <FormField label="Reference / Receipt No." id="income-reference">
              <input id="income-reference" value={values.reference} onChange={(event) => setValues({ ...values, reference: event.target.value })} placeholder="Receipt number or transaction reference" />
            </FormField>
          </div>

          <FormField label="Note" id="income-note">
            <textarea
              id="income-note"
              rows="4"
              value={values.note}
              onChange={(event) => setValues({ ...values, note: event.target.value })}
              placeholder="Optional note for this payment"
            />
          </FormField>

          <div className="form-actions">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" loading={saving}>Save School Fee</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
